import React, { useEffect, useMemo, useState } from 'react';
import GridSearchTemplate from '../../../pages/GridSearchTemplate';
import { Form } from 'semantic-ui-react';
import { Reports } from '../../../api/services/next';
import { GetReservationTotals } from '../../../api/services/pms';
import { DatePicker } from '../../shared/DatePicker';
import HotelsDropDown from '../../shared/HotelsDropDown';
import RoomReservationStatusDropDown from '../../shared/RoomReservationStatusDropDown';
import RoomStatusesDropDown from '../../shared/RoomStatusesDropDown';
import { useTextResources, useMessageResources } from '../../../language';
import CheckboxWrapper from '../../shared/Checkbox';
import moment from 'moment';
import { onError, onWarning } from '../../shared/messages';
import _ from 'lodash';
import formatCurrency from 'format-currency';
import { currencyFormat } from '../../shared/utils';
import { Currencies } from '../../shared/currencies';
import {
    RoomReservationStatusEnums,
    HousekeepingStatusEnums,
    MaintenanceStatusEnums,
} from '../../shared/statustypes';
import { dateFormat, timeFormat } from '../../shared/constants';
import { createExcelHeader } from '../../GridPanel/utility';

const HousekeepingReport = () => {
    const [data, setData] = useState([]);
    const [searchState, setSearchState] = useState({});
    const [currentHotelId, setCurrentHotelId] = useState(window.optima?.user?.CurrentHotel.Id)

    useEffect(() => {
        setCurrentHotelId(window.optima?.user?.CurrentHotel.Id);
    }, [window.optima?.user?.CurrentHotel.Id]);

    const text = useTextResources();
    const msg = useMessageResources();

    const onSearchComplete = async (data, searchState) => {
        if (searchState.ShowReservationTotals) {
            (async () => {
                try {
                    const totals = _.flatMap(
                        await Promise.all(
                            _.map(_.groupBy(data, 'HotelId'), (rows, HotelId) => {
                                return GetReservationTotals({
                                    ReservationIDs: rows.flatMap(
                                        (row) => row.ReservationId || []
                                    ),
                                    HotelId: HotelId,
                                    TargetCurrencyID: window.user?.CurrentHotel?.MainCurrencyId || 1,
                                });
                            })
                        )
                    );

                    const dataWithTotals = data.map((item) => ({
                        ...item,
                        TotalPrice: totals.find(
                            (total) => total.ReservationID == item.ReservationId
                        )?.TotalAmountDebitAfterTax
                    }));
                    setData(dataWithTotals);
                } catch (error) {
                    onError(error);
                }
            })();
        } else {
            setData(data);
        }

        setSearchState(searchState);
    };

    const getDisplayFormattedDate = (index) => {
        const prop = `FromToDates[${index}]`;
        return searchState[prop]
            ? moment(searchState[prop]).format(dateFormat)
            : moment().add(index, 'day').format(dateFormat);
    };

    const excelHeaderTitle = `${text('HousekeepingReport')} ${getDisplayFormattedDate(0)} - ${getDisplayFormattedDate(1)}`;


    const getDateCellValue = (row, columnName) => {
        const date = row[columnName];
        return date && moment(date).format(dateFormat);
    };

    const getTimeCellValue = (row, columnName) => {
        const time = row[columnName];
        return time && moment(time, timeFormat).format('LT');
    };

    const getMaintenanceStatusCellValue = (row, columnName) =>
        text(MaintenanceStatusEnums[row[columnName] || 3]);

    const getHousekeepingStatusCellValue = (row, columnName) =>
        text(HousekeepingStatusEnums[row[columnName] || 2]);

    const getReservationStatusCellValue = (row, columnName) =>
        text(RoomReservationStatusEnums[row[columnName]]);

    const addonsFormatter = (row, columnName) => row[columnName].join(', ');

    const getPaxCellValue = ({
      Adults,
      Children,
      Infants,
      RoomReservationStatus,
    }) => {
      return RoomReservationStatusEnums[RoomReservationStatus] ==
        RoomReservationStatusEnums[1] //If Status is NoReservations, don't show Pax
        ? null
        : `${Adults || 0} / ${Children || 0} / ${Infants || 0}`;
    };

    const totalPriceFormatter = ({ value }) =>
        value
            ? formatCurrency(
                value,
                currencyFormat(
                    window.user?.CurrentHotel?.MainCurrencyId || 1,
                    Currencies
                )
            )
            : null;

    const commentsFormatter = ({ value }) => (
        <div dangerouslySetInnerHTML={{ __html: value }} />
    );

    const getGuestDetailsCellValue = (row, columnName) =>
        searchState.ShowGuestDetails ? row[columnName] : null;

    const HiddenColumnsMemoized = useMemo(() => {
        let columns = [];

        if (!searchState.ShowGuestDetails) {
            columns = [...columns,{columnName: 'GuestFirstName'},{columnName: 'GuestLastName'}, {columnName: 'GuestPhone'}];
        }
        if (!searchState.ShowReservationTotals) {
            columns = [...columns,{columnName:  'TotalPrice'}];
        }
        return columns;
    }, [searchState.ShowGuestDetails, searchState.ShowReservationTotals]);

    const gridOptions = {
        search: {
            title: text('HousekeepingReport'),
            fields: [
                <Form.Field
                    key='HotelIds'
                    multiple
                    label={text('Hotel')}
                    control={HotelsDropDown}
                    clearable
                    allowSelectAll
                />,
                <DatePicker picktype='range' label={text('Dates')} key='FromToDates' />,
                <Form.Field
                    label={text('Status')}
                    control={RoomReservationStatusDropDown}
                    key='RoomReservationStatus'
                    placeholder={text('All')}
                    clearable
                    width='4'
                    allowSelectAll
                    multiple
                />,
                <Form.Field
                    label={text('RoomStatus')}
                    control={RoomStatusesDropDown}
                    key='RoomStatuses'
                    clearable
                    width='4'
                    placeholder={text('All')}
                    allowSelectAll
                    multiple
                />,
                <CheckboxWrapper
                    label={text('ShowGuestDetails')}
                    key='ShowGuestDetails'
                />,
                <CheckboxWrapper
                    label={text('ShowReservationTotals')}
                    key='ShowReservationTotals'
                />,
            ],
            api: Reports.Housekeeping,
            initial: {
                ...currentHotelId && {HotelIds: [ currentHotelId ]},
            },
            spec: {
                MaxResults: 1000,
            },
            validate: (state) => {
                let isValid = state["FromToDates[0]"] && state["FromToDates[1]"];
                if(!isValid)
                    onWarning(msg("FromDateIsRequired"));
                return isValid;
            }
        },
        grid: {
            columns: [
                { name: 'Date', title: text('Date'), getCellValue: getDateCellValue },
                {
                    name: 'RoomReservationStatus',
                    title: text('Status'),
                    getCellValue: getReservationStatusCellValue,
                },
                { name: 'Hotel', title: text('Hotel') },
                { name: 'RoomName', title: text('Room') },
                {
                    name: 'GuestFirstName',
                    title: text('FirstName'),
                    getCellValue: getGuestDetailsCellValue,
                },
                {
                    name: 'GuestLastName',
                    title: text('LastName'),
                    getCellValue: getGuestDetailsCellValue,
                },
                {
                    name: 'GuestPhone',
                    title: text('Phone'),
                    getCellValue: getGuestDetailsCellValue,
                },
                {
                    name: 'Clean',
                    title: text('Clean'),
                    getCellValue: getHousekeepingStatusCellValue,
                },
                {
                    name: 'MaintenanceStatus',
                    title: text('MaintenanceStatus'),
                    getCellValue: getMaintenanceStatusCellValue,
                },
                { name: 'Eta', title: text('ETA'), getCellValue: getTimeCellValue },
                {
                    name: 'CheckInDate',
                    title: text('Arrival'),
                    getCellValue: getDateCellValue,
                },
                { name: 'Etd', title: text('ETD'), getCellValue: getTimeCellValue },
                {
                    name: 'CheckOutDate',
                    title: text('Departure'),
                    getCellValue: getDateCellValue,
                },
                { name: 'Nights', title: text('Nights'), excludePdf: true },
                { name: 'ReservationStatus', title: text('ReservationStatus') },
                {
                    name: 'TotalPrice',
                    title: text('TotalPrice'),
                    formatter: totalPriceFormatter,
                },
                { name: 'Pax', title: text('Pax'), getCellValue: getPaxCellValue },
                {
                    name: 'AddonNames',
                    title: text('Addons'),
                    getCellValue: addonsFormatter,
                },
                {
                    name: 'Comments',
                    title: text('Comments'),
                    formatter: commentsFormatter,
                    html: true,
                },
            ],
            paging: true,
            cacheId: 'Housekeeping',
            customOptions: {
                overrideData:true,
                group: {
                    grouping: [
                    { columnName: 'Date' },
                    { columnName: 'Hotel' },
                    { columnName: 'RoomReservationStatus' }]
                },
                sort: {
                    sorting: [
                        { columnName: 'RoomName', direction: 'asc' },
                        { columnName: 'Hotel', direction: 'asc' },
                    ]
                },
                hidden:{
                    hiddenColumns:HiddenColumnsMemoized,
                    toggleHidden: true
                }
            },
            scrollbarX: true,
            export: true,
            pdf: true,
            excel: {
                customizeHeader: createExcelHeader(excelHeaderTitle),
                grouping: [{ columnName: 'Date' },
                { columnName: 'Hotel' },
                { columnName: 'RoomReservationStatus' }],
                hiddenColumns: HiddenColumnsMemoized
            },
        },
    };

    return (
        <GridSearchTemplate
            options={gridOptions}
            onSearchComplete={onSearchComplete}
            data={data}
        />
    );
};
export default HousekeepingReport;
